import React, { useMemo } from 'react';

import { faTh, faWrench } from '@fortawesome/pro-solid-svg-icons';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';

import Option from '../option_defaults/option';
import PageTemplateStyleSelector from '../option_defaults/page_template_style_selector';
import DefaultOptionMessage from './default_option_message';
import PanelHeader from './panel_header';

export interface PageTemplateStyle {
  identifier: string;
  name: string;
}

export interface PageOptionDefinition {
  name: string;
  style_id?: string | null;
  [key: string]: unknown;
}

interface Props {
  allOptions: PageOptionDefinition[];
  options: Record<string, unknown>;
  styleId: string | null;
  styles: PageTemplateStyle[];
  onOptionsRemove: (names: string[]) => void;
  onOptionsUpdate: (changes: Record<string, unknown>) => void;
  onStyleRemove: () => void;
  onStyleUpdate: (layout: PageTemplateStyle) => void;
}

export default function DefaultBehavior({
  allOptions,
  options,
  styleId,
  styles,
  onOptionsRemove,
  onOptionsUpdate,
  onStyleRemove,
  onStyleUpdate,
}: Props) {
  // Options tied to a layout only show while that layout is selected.
  const filteredOptions = useMemo(
    () => allOptions.filter(option => !option.style_id || !styleId || styleId === option.style_id),
    [allOptions, styleId]
  );

  return (
    <>
      <h6 className="mt-5">Default Behavior</h6>
      <p className="my-3">
        Below, you are able to set the default layout and page options for this page template.
      </p>

      {styles.length > 0 && (
        <Card className="mb-5" id="page-layouts">
          <PanelHeader
            icon={faTh}
            subtitle="Choose which layout shows by default for this page"
            title="Layout"
          />
          <Card.Body>
            <DefaultOptionMessage />
            <PageTemplateStyleSelector
              pageTemplateStyleId={styleId}
              pageTemplateStyles={styles}
              onRemove={onStyleRemove}
              onUpdate={onStyleUpdate}
            />
          </Card.Body>
        </Card>
      )}

      {filteredOptions.length > 0 && (
        <Card className="mb-5" id="page-options">
          <PanelHeader
            icon={faWrench}
            subtitle="Choose which options display by default for this page"
            title="Page Options"
          />
          <Card.Body>
            <DefaultOptionMessage />
            {filteredOptions.map((option, index) => (
              <div className={index < filteredOptions.length - 1 ? 'mb-3' : ''} key={option.name}>
                <Option
                  defaults={options}
                  option={option}
                  onRemove={onOptionsRemove}
                  onUpdate={onOptionsUpdate}
                />
              </div>
            ))}
          </Card.Body>
        </Card>
      )}
    </>
  );
}
